/**
 * @file Header.jsx
 * @author Rishikesh
 * @date 2024-09-09
 * @description Header component of this app.
 */
import React from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Stack,
  Badge,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  Typography,
  Box,
} from "@mui/material";
import {
  AccountCircle,
  MessageRounded,
  // MoreVert,
  NotesOutlined,
  NotificationsRounded,
  PowerSettingsNewOutlined,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { useDrawer } from "../context/DrawerContext";
import { CrossroadsLogo } from "../../assets/images/CrossroadsLogo";
import { signOut } from "../../store/features/authSlice";

const Header = () => {
  const { toggleDrawer } = useDrawer();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const name = useSelector((state) => state.auth.user?.name);
  const role = useSelector((state) => state.auth.user?.role);
  const [openLogoutDialog, setOpenLogoutDialog] = React.useState(false);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleOpenLogoutDialog();
  }

  const handleOpenLogoutDialog = () => {
    setOpenLogoutDialog(true);
  };

  const handleCloseLogoutDialog = () => {
    setOpenLogoutDialog(false);
  };

  const handleSignOut = () => {
    dispatch(signOut());
    navigate('/candidate-login');
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: 1251,
        backgroundColor: "white",
        boxShadow: "none",
      }}
    >
      <Toolbar
        style={{
          justifyContent: "space-between",
        }}
      >
        <Stack direction="row" alignItems="center">
          {role !== 'candidate' ? (
            <IconButton onClick={toggleDrawer}>
              <NotesOutlined fontSize="large" color="#434343" />
            </IconButton>
          ) : (null)}
          <CrossroadsLogo />
        </Stack>
        <Stack direction="row" alignItems="center" columnGap={1.2}>
          <IconButton
            size="medium"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            sx={{ bgcolor: "#E9E9E9" }}
            // onClick={handleMenu}
            color="#434343"
          >
            <Badge color="error" variant="dot">
              <MessageRounded fontSize="24" />
            </Badge>
          </IconButton>
          {role !== 'candidate' ? (
            <>
              <IconButton
                size="medium"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                sx={{ bgcolor: "#E9E9E9" }}
                // onClick={handleMenu}
                color="#434343"
              >
                <Badge color="error" variant="dot">
                  <NotificationsRounded fontSize="24" />
                </Badge>
              </IconButton>
            </>
          ) : (
            <IconButton
              size="medium"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              sx={{ bgcolor: "#E9E9E9" }}
              // onClick={handleMenu}
              color="#434343"
              onClick={handleLogout}
            >
              <PowerSettingsNewOutlined />
            </IconButton>
          )}
          <IconButton
            size="medium"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            sx={{ bgcolor: "#E9E9E9" }}
            onClick={handleMenu}
            color="#434343"
          >
            <AccountCircle fontSize="24" />
          </IconButton>

          {/* <IconButton
            size="medium"
            aria-label="account of current user"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            // onClick={handleMenu}
            color="#434343"
          >
            <MoreVert fontSize="24" />
          </IconButton> */}
          <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={Boolean(anchorEl)}
            onClose={handleClose}
            onClick={handleClose}
            slotProps={{
              paper: {
                elevation: 0,
                sx: {
                  overflow: "visible",
                  filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                  mt: 1.5,
                  "& .MuiAvatar-root": {
                    width: 32,
                    height: 32,
                    ml: -0.5,
                    mr: 1,
                  },
                  "&::before": {
                    content: '""',
                    display: "block",
                    position: "absolute",
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: "background.paper",
                    transform: "translateY(-50%) rotate(45deg)",
                    zIndex: 0,
                  },
                },
              },
            }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            <MenuItem onClick={handleClose} style={{ display: "flex", alignItems: "center" }}>
              <Avatar />
              <Box display="flex" flexDirection="column">
                <Typography variant="body1" fontWeight="bold">
                  {name}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {role}
                </Typography>
              </Box>
            </MenuItem>
          </Menu>
          <Dialog
            open={openLogoutDialog}
            onClose={handleCloseLogoutDialog}
            aria-labelledby="logout-confirm-dialog-title"
            aria-describedby="logout-confirm-dialog-description"
          >
            <DialogTitle id="logout-confirm-dialog-title">
              Confirm Logout
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="logout-confirm-dialog-description">
                Are you sure you want to log out?
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseLogoutDialog} color="primary">
                Cancel
              </Button>
              <Button
                onClick={() => {
                  handleSignOut();
                  handleCloseLogoutDialog();
                }}
                color="primary"
                autoFocus
              >
                Logout
              </Button>
            </DialogActions>
          </Dialog>
        </Stack>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
